'use client';

/**
 * ChatPanelLoader - Data fetching wrapper for schema-driven ChatPanel
 *
 * Handles the different fetch patterns for each entity type:
 * - project: useProjectMessages, useProjectContacts
 * - rfq: useRFQComparison → vendor, useVendorContacts
 * - vendor: useVendorExplore, useVendorContacts
 * - vendor_response: routes to vendor chat
 *
 * @component_role Data Loader
 * @business_rule Fetch messages and contacts using appropriate API based on type
 * @layer presentation
 * @arch_component_type wrapper
 */

import {
  useProjectMessages,
  useProjectContacts,
  useProjectExplore,
  useVendorExplore,
  useVendorContacts,
  useRFQComparison,
  useSendWhatsApp,
  usePostToChatter,
} from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useChatSchema } from '@/lib/stores/total-matrix-store';
import { ChatPanel } from './chat-panel';
import { LoadingState, ErrorState } from './shared';
import type { ChatPanelData } from './chat-section-renderer';
import type { WhatsAppMediaAttachment } from '@/lib/api/types';

/**
 * Extract exclude_platforms from CHAT_SCHEMA for the MESSAGES section.
 * Returns the filter config to pass to the messages API.
 */
function getMessageFilters(schema: { sections: Array<{ type: string; exclude_platforms?: string[]; exclude_author_ids?: number[] }> } | null) {
  if (!schema) return { exclude_platforms: undefined, exclude_author_ids: undefined };

  const messagesSection = schema.sections.find(s => s.type === 'messages');
  return {
    exclude_platforms: messagesSection?.exclude_platforms,
    exclude_author_ids: messagesSection?.exclude_author_ids,
  };
}

interface ChatPanelLoaderProps {
  entityType: string;
  entityId: string;
  projectId?: string | null;
}

export function ChatPanelLoader({ entityType, entityId, projectId }: ChatPanelLoaderProps) {
  // Resolve strategies from capability store
  const sendWhatsAppStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'send_whatsapp'));
  const chatterPostStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'chatter_post'));
  const projectExploreStrategy = useCapabilityStore((s) => s.strategyName('project_intelligence', 'explore'));
  const messagesStrategy = useCapabilityStore((s) => s.strategyName('messaging', 'history'));
  const projectContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'project_contacts'));
  const vendorExploreStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_explore'));
  const vendorContactsStrategy = useCapabilityStore((s) => s.strategyName('graph_explore', 'vendor_contacts'));
  const rfqComparisonStrategy = useCapabilityStore((s) => s.strategyName('rfi', 'comparison'));

  // Mutations (shared across all entity types)
  const sendWhatsApp = useSendWhatsApp(sendWhatsAppStrategy!);
  const postChatter = usePostToChatter(chatterPostStrategy!);

  // Get CHAT_SCHEMA for this entity type to extract message filters
  const chatSchema = useChatSchema(entityType);
  const messageFilters = getMessageFilters(chatSchema);

  // ─────────────────────────────────────────────────────────────────────────────
  // Project data fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const isProject = entityType === 'project';

  const { data: projectExplore } = useProjectExplore(
    projectExploreStrategy!,
    isProject ? { project_id: entityId, include_relationships: [], max_items: 1 } : null
  );

  const {
    data: projectMessages,
    isLoading: projectMessagesLoading,
    error: projectMessagesError,
    refetch: refetchProjectMessages,
  } = useProjectMessages(messagesStrategy!, isProject ? {
    project_id: entityId,
    max_messages: 100,
    exclude_platforms: messageFilters.exclude_platforms,
    exclude_author_ids: messageFilters.exclude_author_ids,
  } : null);

  const { data: projectContacts, isLoading: projectContactsLoading } = useProjectContacts(
    projectContactsStrategy!,
    isProject ? { project_id: entityId } : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Vendor data fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const isVendor = entityType === 'vendor';

  const { data: vendorExplore } = useVendorExplore(
    vendorExploreStrategy!,
    isVendor ? { vendor_id: entityId, include_products: false } : null
  );

  const { data: vendorContacts, isLoading: vendorContactsLoading } = useVendorContacts(
    vendorContactsStrategy!,
    isVendor ? { vendor_id: entityId } : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // RFQ data fetching
  // ─────────────────────────────────────────────────────────────────────────────
  const isRFQ = entityType === 'rfq';

  const { data: rfqData } = useRFQComparison(
    rfqComparisonStrategy!,
    isRFQ ? { rfq_id: entityId } : null
  );

  // RFQCompareResponse has vendors[] array - get first vendor's semantic_id
  const rfqVendorId = rfqData?.vendors?.[0]?.semantic_id;

  const { data: rfqVendorContacts, isLoading: rfqContactsLoading } = useVendorContacts(
    vendorContactsStrategy!,
    isRFQ && rfqVendorId ? { vendor_id: rfqVendorId } : null
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // Vendor Response → delegates to vendor
  // ─────────────────────────────────────────────────────────────────────────────
  const isVendorResponse = entityType === 'vendor_response';
  // For vendor_response, we'd need to fetch the response to get vendor_id
  // For now, show a message - can be enhanced later

  // ─────────────────────────────────────────────────────────────────────────────
  // Build ChatPanelData based on entity type
  // ─────────────────────────────────────────────────────────────────────────────

  // Handle send WhatsApp with entity context
  const handleSendWhatsApp = async (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => {
    let contextModel: 'project.project' | 'purchase.order' | 'res.partner' = 'project.project';
    let contextId: number | null = null;

    if (isProject && projectExplore?.project?.odoo_id) {
      contextModel = 'project.project';  // Neo4j Project syncs from project.project
      contextId = projectExplore.project.odoo_id;
    } else if (isVendor && vendorExplore?.vendor?.odoo_id) {
      contextModel = 'res.partner';
      contextId = vendorExplore.vendor.odoo_id;
    } else if (isRFQ) {
      // RFQCompareResponse doesn't include odoo_id - skip chatter posting for now
      // TODO: Add RFQ odoo_id to comparison response or use separate endpoint
      contextModel = 'purchase.order';
      contextId = null;
    }

    if (!contextId) {
      return { success: false, error: 'Entity details not loaded' };
    }

    try {
      const result = await sendWhatsApp.mutateAsync({
        to: phone,
        message,
        context_model: contextModel,
        context_id: contextId,
        media: media && media.length > 0 ? media : undefined,
      });
      return { success: result.success, error: result.whatsapp_error || undefined };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  };

  // Handle post to chatter (internal notes)
  const handlePostToChatter = async (message: string) => {
    let contextModel: 'project.project' | 'purchase.order' | 'res.partner' = 'project.project';
    let contextId: number | null = null;

    if (isProject && projectExplore?.project?.odoo_id) {
      contextModel = 'project.project';  // Neo4j Project syncs from project.project
      contextId = projectExplore.project.odoo_id;
    } else if (isVendor && vendorExplore?.vendor?.odoo_id) {
      contextModel = 'res.partner';
      contextId = vendorExplore.vendor.odoo_id;
    } else if (isRFQ) {
      // RFQCompareResponse doesn't include odoo_id - skip for now
      contextModel = 'purchase.order';
      contextId = null;
    }

    if (!contextId) {
      return { success: false, error: 'Entity details not loaded' };
    }

    try {
      const result = await postChatter.mutateAsync({
        message,
        context_model: contextModel,
        context_id: contextId,
      });
      return { success: result.success, message_id: result.message_id ?? undefined, error: result.error || undefined };
    } catch (err) {
      return { success: false, error: (err as Error).message };
    }
  };

  // Build data object based on entity type
  let data: ChatPanelData;
  let isLoading = false;
  let error: string | null = null;

  if (isProject) {
    isLoading = projectMessagesLoading;
    error = projectMessagesError ? (projectMessagesError as Error).message : null;
    data = {
      messages: projectMessages?.messages || [],
      messagesLoading: projectMessagesLoading,
      onRefreshMessages: refetchProjectMessages,
      messagesHeader: 'PROJECT MESSAGES',
      messagesMetadata: projectMessages?.metadata,
      contacts: projectContacts?.contacts || [],
      contactsLoading: projectContactsLoading,
      onSendWhatsApp: handleSendWhatsApp,
      isSending: sendWhatsApp.isPending,
      onPostToChatter: handlePostToChatter,
      isPostingChatter: postChatter.isPending,
    };
  } else if (isVendor) {
    data = {
      messages: [], // TODO: vendor messages endpoint
      messagesHeader: 'VENDOR MESSAGES',
      contacts: vendorContacts?.contacts || [],
      contactsLoading: vendorContactsLoading,
      onSendWhatsApp: handleSendWhatsApp,
      isSending: sendWhatsApp.isPending,
      onPostToChatter: handlePostToChatter,
      isPostingChatter: postChatter.isPending,
    };
  } else if (isRFQ) {
    data = {
      messages: [], // TODO: RFQ messages
      messagesHeader: 'RFQ MESSAGES',
      contacts: rfqVendorContacts?.contacts || [],
      contactsLoading: rfqContactsLoading,
      onSendWhatsApp: handleSendWhatsApp,
      isSending: sendWhatsApp.isPending,
      onPostToChatter: handlePostToChatter,
      isPostingChatter: postChatter.isPending,
    };
  } else if (isVendorResponse) {
    // Vendor response delegates to vendor - for now show placeholder
    data = {
      messages: [],
      messagesHeader: 'RESPONSE MESSAGES',
      contacts: [],
      onSendWhatsApp: handleSendWhatsApp,
      isSending: sendWhatsApp.isPending,
      onPostToChatter: handlePostToChatter,
      isPostingChatter: postChatter.isPending,
    };
  } else {
    // Unknown entity type - show minimal UI
    data = {
      messages: [],
      contacts: [],
      onSendWhatsApp: handleSendWhatsApp,
      isSending: sendWhatsApp.isPending,
      onPostToChatter: handlePostToChatter,
      isPostingChatter: postChatter.isPending,
    };
  }

  if (isLoading) {
    return <LoadingState message="Loading messages..." />;
  }

  if (error) {
    return <ErrorState message="Failed to load messages" detail={error} />;
  }

  return <ChatPanel entityType={entityType} entityId={entityId} data={data} />;
}
